import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiFetch, subirBinario, descargarArchivo } from '../lib/api';
import { capInfo } from '../lib/capitulos';
import { Spinner } from '../components/Spinner';
import { useConfirm } from '../components/ConfirmDialog';
import { Modal } from '../components/Modal';
import { AYUDA_PROCEDENCIA, ListaFuentes, componentesMarcas, conMarcas, type FuenteProcedencia } from '../lib/procedencia';
import { componentesEnlaces, normalizarUrlsSea } from '../lib/enlaces';

/**
 * Chat con Andrés — rediseño "el chat ES la interfaz" (2026-07-21, spec del estudio UX):
 * cero paneles permanentes. El bloque de título de la cabecera ES el conmutador de
 * conversaciones (patrón app de mensajería); memoria de largo plazo UNIFICADA que Memo
 * alimenta solo en cada compactación (el humano solo corrige); capítulos y acciones en
 * sheets desde el menú ⋯; chat a lo ancho en columna centrada de 820px. El trabajo
 * pesado corre en el worker; la UI hace poll y suaviza el streaming.
 */

interface Mensaje {
  id: string;
  rol: 'user' | 'andres';
  contenido: string;
  citas: { ruta: string; nombre: string; extracto: string }[] | null;
  eventos: { t: 'pensamiento' | 'herramienta' | 'aviso' | 'traza'; d: string }[] | null;
  adjuntos?: { nombre: string; storage_path: string }[] | null;
  acciones?: { tipo: 'generar_capitulo' | 'comprimir_contexto' | 'pdf_respuesta' | 'responder'; capitulo?: string; instrucciones?: string; etiqueta: string; recomendada?: boolean }[] | null;
  estado?: 'generando' | 'listo' | 'error';
  created_at: string;
}

interface ChatInfo { id: string; titulo: string | null; created_at?: string; resumen_contexto?: string | null; compactado_hasta?: string | null }

interface Borrador {
  id: string;
  capitulo: string;
  version: number;
  agent_code: string;
  estado: 'generando' | 'listo' | 'error' | string;
  error?: string | null;
  est_cost?: number | null;
  created_at: string;
  contenido?: string;
  fuentes?: FuenteProcedencia[] | null;
}


interface Contexto {
  tokens_in: number; tokens_out: number; costo_usd: number;
  usado: number; limite: number; pct: number; compactando: boolean;
}

const nf = new Intl.NumberFormat('es-CL');

const fechaRel = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const dias = Math.floor((hoy.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000);
  if (dias <= 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
};

/** Revela el texto progresivamente aunque llegue en pedazos (poll): streaming suave. */
function TextoFluido({ texto, generando }: { texto: string; generando: boolean }) {
  const [visible, setVisible] = useState(generando ? '' : texto);
  const objetivoRef = useRef(texto);
  objetivoRef.current = texto;

  useEffect(() => {
    if (!generando) { setVisible(texto); return; }
    const t = window.setInterval(() => {
      setVisible((v) => {
        const obj = objetivoRef.current;
        if (!obj.startsWith(v)) return obj;
        if (v.length >= obj.length) return v;
        const paso = Math.max(2, Math.ceil((obj.length - v.length) / 25));
        return obj.slice(0, v.length + paso);
      });
    }, 35);
    return () => window.clearInterval(t);
  }, [generando, texto]);

  return (
    <div className="chat-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentesEnlaces}>{normalizarUrlsSea(visible) + (generando ? ' ▌' : '')}</ReactMarkdown>
    </div>
  );
}

/** Timeline de trabajo de Andrés: pensamiento colapsable + acciones en orden. */
function TimelineEventos({ m }: { m: Mensaje }) {
  const eventos = m.eventos ?? [];
  const pensamiento = eventos.filter((e) => e.t === 'pensamiento').at(-1);
  const acciones = eventos.filter((e) => e.t !== 'pensamiento' && e.t !== 'traza');
  if (!pensamiento && acciones.length === 0) return null;
  return (
    <div className="mb-1.5 flex flex-col gap-1.5">
      {pensamiento && (
        <details open={m.estado === 'generando'} className="rounded-xl border border-slate-200 bg-[#FAFBFC] px-3 py-2">
          <summary className="cursor-pointer text-[11px] font-medium text-gris">
            💭 Pensamiento{m.estado === 'generando' ? '…' : ''}
          </summary>
          <p className="mt-1 whitespace-pre-wrap text-[11.5px] font-light italic leading-relaxed text-gris">{pensamiento.d}</p>
        </details>
      )}
      {acciones.length > 0 && (
        <ol className="ml-1 flex flex-col gap-1 border-l-2 border-celeste/50 pl-3">
          {acciones.map((e, i) => {
            const activo = i === acciones.length - 1 && m.estado === 'generando';
            return (
              <li key={i} className="relative text-[11.5px] font-normal leading-relaxed">
                {activo ? (
                  <span aria-hidden="true" className="absolute -left-[22px] top-[1px]"><Spinner tamano="xs" className="!h-3.5 !w-3.5" /></span>
                ) : (
                  <span aria-hidden="true" className="absolute -left-[19px] top-[5px] h-2 w-2 rounded-full bg-celeste" />
                )}
                <span className={e.t === 'aviso' ? 'text-amber-700' : activo ? 'font-medium text-azul-profundo' : 'text-azul-noche/80'}>{e.d}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Indicador de actividad: mini spinner + etiqueta viva de lo que hace el agente. Reemplaza
 *  el placeholder "…" del arranque y comunica que hay trabajo en curso (2026-07-24). */
function IndicadorActividad({ m, agente }: { m: Mensaje; agente: string }) {
  const ultimaAccion = (m.eventos ?? []).filter((e) => e.t !== 'pensamiento').at(-1);
  const label = ultimaAccion?.d ?? `${agente} está pensando`;
  return (
    <div className="mr-auto inline-flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-4 py-2.5" role="status" aria-live="polite">
      <Spinner tamano="sm" />
      <span className="text-[12.5px] font-normal text-gris">{label.replace(/…$/, '')}<span className="animate-pulse">…</span></span>
    </div>
  );
}

export default function ChatAndres({ favoritoId, nombre, estudiando, onVolver, chatInicialId, adjuntoInicial, textoInicial, agente = 'Andrés', placeholderVacio, badgesIniciales, sinCapitulos = false, etiquetaMemoria = 'Memoria de la DIA', vacioExtra, claseAltura = 'h-[calc(100dvh-165px)] min-h-[440px] lg:h-[calc(100dvh-130px)]' }: {
  favoritoId: string;
  nombre: string;
  estudiando?: boolean;
  /** Volver a la página que abrió el chat. Si no se pasa, el botón ← no se muestra (chats que SON la página, p. ej. Investigador SEIA). */
  onVolver?: () => void;
  /** Abrir un chat específico (🤖 del explorador: chat enfocado en un archivo). */
  chatInicialId?: string;
  /** Adjunto pre-cargado en el composer (archivo fuera del corpus bajado de SharePoint). */
  adjuntoInicial?: { nombre: string; storage_path: string } | null;
  /** Texto pre-escrito en el composer. */
  textoInicial?: string;
  /** Nombre del agente (Andrés por defecto; Emilia en la ventanilla del Newsletter). */
  agente?: string;
  /** Texto del estado vacío (override del placeholder de Andrés). */
  placeholderVacio?: string;
  /** Shortcuts clickeables en el chat vacío: al tocar, prellenan el composer. */
  badgesIniciales?: string[];
  /** Oculta todo lo de capítulos (panel derecho, menú 📝, redactar): agentes que no redactan DIA (Investigador SEIA). */
  sinCapitulos?: boolean;
  /** Etiqueta de la memoria de largo plazo ("Memoria de la DIA" por defecto). */
  etiquetaMemoria?: string;
  /** Contenido adicional bajo los ejemplos del estado vacío (notas de uso del agente). */
  vacioExtra?: ReactNode;
  /** Clases de alto del contenedor raíz (por defecto, página completa bajo la cabecera del layout). */
  claseAltura?: string;
}) {
  const confirmar = useConfirm();
  const [chats, setChats] = useState<ChatInfo[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  // Espejo en ref: permite habilitar el botón de enviar de inmediato (con texto) y esperar a que
  // la conversación termine de abrirse/crearse — antes el botón quedaba bloqueado por `!chatId`
  // durante el ida-y-vuelta inicial, y "tardaba bastante" en habilitarse (feedback grafo→Plus).
  const chatIdRef = useRef<string | null>(null);
  useEffect(() => { chatIdRef.current = chatId; }, [chatId]);
  const esperarChatId = () => new Promise<string | null>((resolve) => {
    if (chatIdRef.current) return resolve(chatIdRef.current);
    const t0 = Date.now();
    const iv = window.setInterval(() => {
      if (chatIdRef.current) { window.clearInterval(iv); resolve(chatIdRef.current); }
      else if (Date.now() - t0 > 10000) { window.clearInterval(iv); resolve(null); }
    }, 100);
  });
  const [chatInfo, setChatInfo] = useState<ChatInfo | null>(null);
  const [contexto, setContexto] = useState<Contexto | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [pensando, setPensando] = useState(false);
  const [texto, setTexto] = useState(textoInicial ?? '');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Paneles flotantes de la cabecera y sheets
  const [panel, setPanel] = useState<'chats' | 'medidor' | 'menu' | null>(null);
  const [sheet, setSheet] = useState<'memoria' | 'borradores' | null>(null);
  const [instruccionesCap, setInstruccionesCap] = useState('');
  // Conversaciones: edición inline (sin window.prompt/confirm)
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [tituloEdit, setTituloEdit] = useState('');
  const [confirmando, setConfirmando] = useState<string | null>(null); // chatId o '__todo__'
  // Memoria unificada
  const [memoria, setMemoria] = useState('');
  const [memoriaAt, setMemoriaAt] = useState<string | null>(null);
  const [editandoMemoria, setEditandoMemoria] = useState(false);
  const [memoriaDraft, setMemoriaDraft] = useState('');
  const [guardandoMemoria, setGuardandoMemoria] = useState(false);
  // Capítulos / adjuntos
  const [borradores, setBorradores] = useState<Borrador[]>([]);
  const [verBorrador, setVerBorrador] = useState<Borrador | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [adjuntos, setAdjuntos] = useState<{ nombre: string; storage_path: string }[]>(adjuntoInicial ? [adjuntoInicial] : []);
  const [subiendo, setSubiendo] = useState(false);
  const adjRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Auto-crecimiento REAL del composer: alto por contenido (con wrap), no por saltos de
  // línea — una ruta larga en una sola línea antes quedaba aplastada (feedback 22-jul).
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 192)}px`;
  }, [texto]);
  const primeraCargaRef = useRef(true);
  const finRef = useRef<HTMLDivElement>(null);
  const contRef = useRef<HTMLDivElement>(null);

  const cargarChats = useCallback(async (): Promise<ChatInfo[]> => {
    const d = await apiFetch<{ chats: ChatInfo[] }>(`/api/dia/${favoritoId}/chats`);
    setChats(d.chats);
    return d.chats;
  }, [favoritoId]);

  const cargarMemoria = useCallback(async () => {
    try {
      const m = await apiFetch<{ memoria: { contenido: string; updated_at: string | null } }>(`/api/dia/${favoritoId}/memoria`);
      setMemoria(m.memoria.contenido);
      setMemoriaAt(m.memoria.updated_at);
    } catch { /* accesorio */ }
  }, [favoritoId]);

  const cargarBorradores = useCallback(async () => {
    if (sinCapitulos) return;
    try {
      const d = await apiFetch<{ borradores: Borrador[] }>(`/api/dia/${favoritoId}/capitulos`);
      setBorradores(d.borradores);
    } catch { /* accesorio */ }
  }, [favoritoId, sinCapitulos]);

  // Al entrar: conversaciones (abre la más reciente o crea la primera) + memoria + borradores.
  useEffect(() => {
    void (async () => {
      try {
        const lista = await cargarChats();
        if (chatInicialId) setChatId(chatInicialId);
        else if (lista.length > 0) setChatId(lista[0]!.id);
        else {
          const c = await apiFetch<{ chat: ChatInfo }>(`/api/dia/${favoritoId}/chats`, { method: 'POST', body: JSON.stringify({}) });
          setChats([c.chat]);
          setChatId(c.chat.id);
        }
        void cargarMemoria();
        void cargarBorradores();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir el chat.');
      }
    })();
  }, [favoritoId, cargarChats, cargarMemoria, cargarBorradores]);

  const cargarMensajes = useCallback(async () => {
    if (!chatId) return;
    try {
      const d = await apiFetch<{ mensajes: Mensaje[]; pensando: boolean; chat: ChatInfo | null; contexto: Contexto }>(`/api/dia/chats/${chatId}/mensajes`);
      setMensajes(d.mensajes);
      setChatInfo(d.chat);
      setContexto(d.contexto);
      setPensando(d.pensando || d.mensajes.some((m) => m.estado === 'generando'));
    } catch { /* siguiente tick */ }
  }, [chatId]);

  useEffect(() => { primeraCargaRef.current = true; setMensajes([]); setChatInfo(null); setContexto(null); void cargarMensajes(); }, [cargarMensajes]);
  useEffect(() => {
    if (!pensando && !contexto?.compactando) return;
    const t = window.setInterval(() => void cargarMensajes(), 1000);
    return () => window.clearInterval(t);
  }, [pensando, contexto?.compactando, cargarMensajes]);

  const hayGenerando = borradores.some((b) => b.estado === 'generando');
  useEffect(() => {
    if (!hayGenerando) return;
    const t = window.setInterval(() => {
      void cargarBorradores();
      setVerBorrador((v) => v && v.estado === 'generando'
        ? (void apiFetch<{ borrador: Borrador }>(`/api/dia/capitulos/${v.id}`).then((d) => setVerBorrador(d.borrador)).catch(() => undefined), v)
        : v);
    }, 2500);
    return () => window.clearInterval(t);
  }, [hayGenerando, cargarBorradores]);

  // Cierre del ciclo: cuando un borrador que estaba 'generando' pasa a 'listo' (o 'error'),
  // avisamos explícitamente — así el usuario SABE que terminó y no cree que se cayó.
  const generandoPrev = useRef<Set<string>>(new Set());
  useEffect(() => {
    const ahora = new Set(borradores.filter((b) => b.estado === 'generando').map((b) => b.id));
    for (const id of generandoPrev.current) {
      if (ahora.has(id)) continue;
      const b = borradores.find((x) => x.id === id);
      if (!b) continue;
      if (b.estado === 'listo') {
        setAviso(`✅ ${capInfo(b.capitulo).titulo} listo (v${b.version}) — ábrelo en 📝 Borradores.`);
        window.setTimeout(() => setAviso(null), 9000);
      } else if (b.estado === 'error') {
        setError(`La generación del ${capInfo(b.capitulo).titulo} falló${b.error ? `: ${b.error}` : ''}. Puedes reintentar.`);
      }
    }
    generandoPrev.current = ahora;
  }, [borradores]);

  // Escape cierra paneles y sheets (accesibilidad del patrón dropdown/sheet).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setPanel(null); setConfirmando(null); setRenombrando(null); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Auto-scroll inteligente: sigue el streaming, respeta al lector que subió.
  const seguirFondo = useCallback(() => {
    const c = contRef.current;
    if (!c) return;
    if (c.scrollHeight - c.scrollTop - c.clientHeight < 200) c.scrollTop = c.scrollHeight;
  }, []);
  useEffect(() => {
    const c = contRef.current;
    if (!c) return;
    // Primera carga de la conversación: aterrizar DIRECTO en lo último (sin animación).
    if (primeraCargaRef.current && mensajes.length > 0) {
      primeraCargaRef.current = false;
      c.scrollTop = c.scrollHeight;
      return;
    }
    if (c.scrollHeight - c.scrollTop - c.clientHeight < 200) finRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensajes]);
  useEffect(() => {
    if (!pensando) return;
    const t = window.setInterval(seguirFondo, 250);
    return () => window.clearInterval(t);
  }, [pensando, seguirFondo]);

  // ── Acciones ──
  async function subirAdjuntos(archivos: File[] | FileList) {
    if (!chatId) return;
    setSubiendo(true);
    setError(null);
    try {
      for (const f of Array.from(archivos).slice(0, 6 - adjuntos.length)) {
        const d = await subirBinario<{ adjunto: { nombre: string; storage_path: string } }>(
          `/api/dia/chats/${chatId}/adjuntos?nombre=${encodeURIComponent(f.name || 'imagen-pegada.png')}`, f);
        setAdjuntos((a) => [...a, d.adjunto]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo subir el adjunto.');
    } finally {
      setSubiendo(false);
    }
  }

  function alPegar(e: React.ClipboardEvent) {
    const imgs = Array.from(e.clipboardData.items)
      .filter((i) => i.type.startsWith('image/'))
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
    if (imgs.length) { e.preventDefault(); void subirAdjuntos(imgs); }
  }

  async function cancelar() {
    if (!chatId) return;
    try { await apiFetch(`/api/dia/chats/${chatId}/cancelar`, { method: 'POST' }); } catch { /* la respuesta terminará sola */ }
  }

  async function enviar(textoOverride?: string) {
    // Se PERMITE enviar mientras el agente responde: el mensaje se encola y se contesta en
    // orden (no toca el LLM, solo la cola — funciona con cualquier modelo).
    // `textoOverride`: respuesta de un chip de aclaración (tipo "responder") — se envía tal cual.
    const fuente = textoOverride ?? texto;
    if (!fuente.trim() || subiendo) return;
    const contenido = fuente.trim();
    const adj = textoOverride ? [] : adjuntos;
    // Si la conversación aún se está abriendo, esperamos a que exista (hasta 10s) en vez de dejar
    // el botón muerto: el usuario puede tocar "enviar" apenas ve el texto prellenado.
    const id = chatId ?? (await esperarChatId());
    if (!id) { setError('Aún estamos abriendo el chat, intenta enviar de nuevo en un momento.'); return; }
    if (!textoOverride) { setTexto(''); setAdjuntos([]); } // un chip no borra el borrador del composer
    setError(null);
    setMensajes((m) => [...m, { id: `tmp-${Date.now()}`, rol: 'user', contenido, citas: null, eventos: null, adjuntos: adj, created_at: new Date().toISOString() }]);
    setPensando(true);
    try {
      await apiFetch(`/api/dia/chats/${id}/mensajes`, { method: 'POST', body: JSON.stringify({ contenido, adjuntos: adj.length ? adj : undefined }) });
      void cargarChats();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo enviar.');
      setPensando(false);
      if (!textoOverride) setTexto(contenido); // no perder lo escrito si el envío falla
    }
  }

  async function nuevoChat() {
    try {
      const c = await apiFetch<{ chat: ChatInfo }>(`/api/dia/${favoritoId}/chats`, { method: 'POST', body: JSON.stringify({}) });
      await cargarChats();
      setChatId(c.chat.id);
      setPanel(null);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo crear.'); }
  }

  async function guardarTitulo(c: ChatInfo) {
    const titulo = tituloEdit.trim();
    setRenombrando(null);
    if (!titulo || titulo === c.titulo) return;
    try {
      await apiFetch(`/api/dia/chats/${c.id}`, { method: 'PATCH', body: JSON.stringify({ titulo }) });
      await cargarChats();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo renombrar.'); }
  }

  async function borrarChat(c: ChatInfo) {
    setConfirmando(null);
    try {
      await apiFetch(`/api/dia/chats/${c.id}`, { method: 'DELETE' });
      const lista = await cargarChats();
      if (chatId === c.id) setChatId(lista[0]?.id ?? null);
      if (lista.length === 0) await nuevoChat();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo borrar.'); }
  }

  async function borrarTodo() {
    setConfirmando(null);
    setPanel(null);
    try {
      await apiFetch(`/api/dia/${favoritoId}/chats`, { method: 'DELETE' });
      setChats([]);
      setChatId(null);
      await nuevoChat();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo borrar el historial.'); }
  }

  async function comprimir() {
    if (!chatId) return;
    setPanel(null);
    try {
      await apiFetch(`/api/dia/chats/${chatId}/compactar`, { method: 'POST' });
      setAviso('🗜 Memo está comprimiendo — lo esencial quedará en la Memoria de la DIA.');
      window.setTimeout(() => setAviso(null), 6000);
      void cargarMensajes();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo comprimir.'); }
  }

  async function guardarMemoria() {
    setGuardandoMemoria(true);
    try {
      await apiFetch(`/api/dia/${favoritoId}/memoria`, { method: 'PUT', body: JSON.stringify({ contenido: memoriaDraft }) });
      setMemoria(memoriaDraft);
      setMemoriaAt(new Date().toISOString());
      setEditandoMemoria(false);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo guardar la memoria.'); }
    finally { setGuardandoMemoria(false); }
  }

  async function generarCapitulo(capitulo: string, instrucciones?: string) {
    try {
      await apiFetch(`/api/dia/${favoritoId}/capitulos`, { method: 'POST', body: JSON.stringify({ capitulo, instrucciones: instrucciones?.trim() || undefined }) });
      setAviso(`📝 ${capInfo(capitulo).agente} está redactando el ${capInfo(capitulo).titulo}${instrucciones?.trim() ? ' (con tus instrucciones)' : ''} — suele tardar 2-3 min; puedes seguir conversando.`);
      window.setTimeout(() => setAviso(null), 6000);
      await cargarBorradores();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo generar.'); }
  }

  async function abrirBorrador(b: Borrador) {
    try {
      const d = await apiFetch<{ borrador: Borrador }>(`/api/dia/capitulos/${b.id}`);
      setVerBorrador(d.borrador);
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo abrir.'); }
  }

  async function eliminarBorrador(b: Borrador) {
    try {
      await apiFetch(`/api/dia/capitulos/${b.id}`, { method: 'DELETE' });
      setVerBorrador(null);
      await cargarBorradores();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo eliminar.'); }
  }

  async function publicarEnSharePoint(b: Borrador) {
    setPublicando(true);
    try {
      const r = await apiFetch<{ ruta: string; webUrl: string | null }>(`/api/dia/capitulos/${b.id}/sharepoint`, { method: 'POST' });
      setAviso(`💾 Guardado en SharePoint → ${r.ruta.split('/').slice(-2).join('/')}`);
      window.setTimeout(() => setAviso(null), 8000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo publicar.');
    } finally {
      setPublicando(false);
    }
  }

  async function borrarMensaje(m: Mensaje) {
    if (!(await confirmar({ titulo: m.rol === 'user' ? 'Borrar mensaje' : 'Borrar respuesta', mensaje: m.rol === 'user' ? '¿Borrar este mensaje y su respuesta?' : `¿Borrar esta respuesta de ${agente}?`, textoAceptar: 'Borrar', destructivo: true }))) return;
    try {
      await apiFetch(`/api/dia/mensajes/${m.id}`, { method: 'DELETE' });
      await cargarMensajes();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo borrar.'); }
  }

  async function reenviarMensaje(m: Mensaje, contenido?: string) {
    if (!contenido && !(await confirmar({ titulo: 'Relanzar conversación', mensaje: `Se borra todo lo posterior a este punto y ${agente} responde de nuevo. ¿Continuar?`, textoAceptar: 'Relanzar', destructivo: true }))) return;
    setEditandoMsg(null);
    try {
      await apiFetch(`/api/dia/mensajes/${m.id}/reenviar`, { method: 'POST', body: JSON.stringify(contenido ? { contenido } : {}) });
      setPensando(true);
      await cargarMensajes();
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo relanzar.'); }
  }

  /** Descarga una respuesta de Andrés como PDF REAL (lo genera el servidor). */
  const [pdfDescargando, setPdfDescargando] = useState<string | null>(null);
  const [editandoMsg, setEditandoMsg] = useState<string | null>(null);
  const [msgDraft, setMsgDraft] = useState('');
  async function descargarPdfMensaje(m: Mensaje) {
    setPdfDescargando(m.id);
    try {
      await descargarArchivo(`/api/dia/mensajes/${m.id}/pdf`, `${agente} — ${nombre}.pdf`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar el PDF.');
    } finally {
      setPdfDescargando(null);
    }
  }

  const chatActivo = chats.find((c) => c.id === chatId) ?? null;
  const compactadoHasta = chatInfo?.compactado_hasta ? new Date(chatInfo.compactado_hasta).getTime() : null;
  const esCompactado = (m: Mensaje) => compactadoHasta !== null && new Date(m.created_at).getTime() <= compactadoHasta;
  const ultimoCompactado = compactadoHasta === null ? null : mensajes.filter(esCompactado).at(-1)?.id ?? null;
  const mensajesListos = mensajes.filter((m) => m.estado !== 'generando').length;
  const colorMedidor = (contexto?.pct ?? 0) < 40 ? 'bg-emerald-500' : (contexto?.pct ?? 0) < 70 ? 'bg-amber-400' : 'bg-red-500';
  const memoriaFresca = memoriaAt !== null && Date.now() - new Date(memoriaAt).getTime() < 86_400_000;

  return (
    <div className={`relative flex ${claseAltura}`}>
      <div className="flex min-w-0 flex-1 flex-col">
      {/* ── Cabecera sticky: volver · conmutador · medidor · memoria · ⋯ ── */}
      <header className="relative z-20 flex h-14 shrink-0 items-center gap-2 border-b border-slate-200/70 bg-white/85 px-1 backdrop-blur sm:px-2">
        {onVolver && <button type="button" onClick={onVolver} className="btn-fantasma !h-9 !w-9 shrink-0 !px-0" title="Volver a la página DIA" aria-label="Volver">←</button>}

        <button
          type="button"
          onClick={() => setPanel(panel === 'chats' ? null : 'chats')}
          aria-expanded={panel === 'chats'}
          aria-haspopup="listbox"
          className="min-w-0 flex-1 rounded-xl px-2 py-1 text-left transition-colors hover:bg-celeste/10 lg:max-w-md lg:flex-initial"
        >
          <span className="block truncate text-[10.5px] font-normal text-gris">{agente} · {nombre}</span>
          <span className="flex items-center gap-1 truncate text-[13.5px] font-medium text-azul-noche">
            <span className="truncate">{chatActivo?.titulo ?? 'Nueva conversación'}</span>
            <span aria-hidden="true" className={`shrink-0 text-[10px] text-gris transition-transform ${panel === 'chats' ? 'rotate-180' : ''}`}>▾</span>
          </span>
        </button>

        <span className="flex-1 lg:block" />

        {contexto && (
          <button
            type="button"
            onClick={() => setPanel(panel === 'medidor' ? null : 'medidor')}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 text-[11px] font-normal text-gris transition-colors hover:bg-celeste/10"
            title="Uso de la ventana de contexto — toca para detalle y comprimir"
          >
            <span className="h-1.5 w-14 overflow-hidden rounded-full bg-slate-200">
              <span className={`block h-full rounded-full transition-[width] duration-700 ease-out ${colorMedidor}`} style={{ width: `${Math.max(2, contexto.pct)}%` }} />
            </span>
            {contexto.pct}%
            {contexto.compactando && <Spinner tamano="xs" />}
          </button>
        )}

        <button
          type="button"
          onClick={() => { setSheet('memoria'); setPanel(null); }}
          className="relative hidden shrink-0 items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[12px] font-medium text-azul-profundo transition-colors hover:bg-celeste/10 lg:flex"
          title={`${etiquetaMemoria} — memoria de largo plazo (Memo la escribe solo)`}
        >
          🧠 Memoria
          {memoriaFresca && <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full bg-azul-profundo" />}
        </button>

        <button type="button" onClick={() => setPanel(panel === 'menu' ? null : 'menu')} aria-expanded={panel === 'menu'} className="btn-fantasma !h-9 !w-9 shrink-0 !px-0" title="Más opciones" aria-label="Más opciones">⋯</button>

        {/* Overlay de cierre de paneles */}
        {panel && <button type="button" aria-label="Cerrar" onClick={() => { setPanel(null); setConfirmando(null); setRenombrando(null); }} className="fixed inset-0 z-20 cursor-default" />}

        {/* ── Panel: conversaciones ── */}
        {panel === 'chats' && (
          <div className={`absolute ${onVolver ? 'left-10' : 'left-1'} top-[calc(100%+6px)] z-30 w-[min(22rem,calc(100vw-4rem))] rounded-2xl border border-slate-200 bg-white p-2 shadow-lg`}>
            <div className="flex items-center justify-between gap-2 px-2 py-1">
              <span className="text-[13px] font-medium text-azul-noche">Conversaciones</span>
              <button type="button" onClick={() => void nuevoChat()} className="btn-secundario !h-8 !text-[12.5px]">+ Nueva</button>
            </div>
            <p className="px-2 pb-1 text-[10.5px] font-light text-gris">Historial compartido: todo el equipo ve estas conversaciones.</p>
            <ul className="max-h-[50vh] overflow-y-auto">
              {chats.map((c) => (
                <li key={c.id} className="group">
                  {renombrando === c.id ? (
                    <input
                      autoFocus
                      value={tituloEdit}
                      onChange={(e) => setTituloEdit(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void guardarTitulo(c); if (e.key === 'Escape') setRenombrando(null); }}
                      onBlur={() => void guardarTitulo(c)}
                      className="campo !h-9 w-full !text-[13px]"
                      aria-label="Nuevo nombre de la conversación"
                    />
                  ) : confirmando === c.id ? (
                    <div className="flex items-center justify-between gap-2 rounded-[10px] bg-red-50 px-3 py-2">
                      <span className="text-[12px] font-normal text-red-700">¿Borrar esta conversación?</span>
                      <span className="flex gap-1.5">
                        <button type="button" onClick={() => void borrarChat(c)} className="rounded-lg bg-red-600 px-2.5 py-1 text-[11.5px] font-medium text-white">Borrar</button>
                        <button type="button" onClick={() => setConfirmando(null)} className="rounded-lg px-2 py-1 text-[11.5px] text-gris">Cancelar</button>
                      </span>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1 rounded-[10px] px-1 ${c.id === chatId ? 'bg-celeste/15' : 'hover:bg-slate-50'}`}>
                      <button type="button" onClick={() => { setChatId(c.id); setPanel(null); }} className="min-w-0 flex-1 px-2 py-2 text-left">
                        <span className={`block truncate text-[13px] ${c.id === chatId ? 'font-medium text-azul-profundo' : 'font-normal text-texto'}`}>{c.titulo ?? 'Sin título'}</span>
                        <span className="block text-[10.5px] font-light text-gris">{fechaRel(c.created_at)}</span>
                      </button>
                      {c.id === chatId && <span aria-hidden="true" className="text-[12px] text-azul-profundo">✓</span>}
                      <button type="button" onClick={() => { setRenombrando(c.id); setTituloEdit(c.titulo ?? ''); }} className="hidden rounded p-1.5 text-[11px] text-gris hover:text-azul-profundo group-hover:block" title="Renombrar" aria-label={`Renombrar ${c.titulo ?? 'conversación'}`}>✎</button>
                      <button type="button" onClick={() => setConfirmando(c.id)} className="hidden rounded p-1.5 text-[11px] text-gris hover:text-red-600 group-hover:block" title="Borrar" aria-label={`Borrar ${c.titulo ?? 'conversación'}`}>✕</button>
                    </div>
                  )}
                </li>
              ))}
              {chats.length === 0 && <li className="px-3 py-4 text-center text-[12.5px] font-light text-gris">Aún no hay conversaciones — parte una nueva.</li>}
            </ul>
          </div>
        )}

        {/* ── Panel: medidor de contexto ── */}
        {panel === 'medidor' && contexto && (
          <div className="absolute right-10 top-[calc(100%+6px)] z-30 w-64 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-lg">
            <p className="text-[12px] font-medium text-azul-noche">Ventana de contexto</p>
            <p className="mt-1 text-[11.5px] font-normal text-gris">Último turno: {nf.format(contexto.usado)} de {nf.format(contexto.limite)} tokens ({contexto.pct}%).</p>
            <div className="mt-2 flex flex-col gap-1 text-[11.5px] font-normal text-gris">
              <span>↓ entrada acumulada: {nf.format(contexto.tokens_in)}</span>
              <span>↑ salida acumulada: {nf.format(contexto.tokens_out)}</span>
              <span>Costo del chat: US$ {contexto.costo_usd.toFixed(2)}</span>
            </div>
            {contexto.compactando ? (
              <p className="mt-3 flex items-center gap-1.5 text-[12px] font-medium text-azul-profundo"><Spinner tamano="xs" /> Memo comprimiendo…</p>
            ) : (
              <button type="button" onClick={() => void comprimir()} disabled={mensajesListos < 6} title={mensajesListos < 6 ? 'Con pocos mensajes no hay nada que comprimir' : 'Memo destila lo antiguo en la memoria — el chat sigue completo a la vista'} className="btn-secundario mt-3 !h-8 w-full !text-[12px] disabled:opacity-50">
                🗜 Comprimir ahora
              </button>
            )}
          </div>
        )}

        {/* ── Panel: menú ⋯ ── */}
        {panel === 'menu' && (
          <div className="absolute right-1 top-[calc(100%+6px)] z-30 w-60 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-lg">
            <button type="button" onClick={() => { setSheet('memoria'); setPanel(null); }} className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[13px] font-normal text-texto hover:bg-slate-50">🧠 {etiquetaMemoria}</button>
            {!sinCapitulos && <button type="button" onClick={() => { setSheet('borradores'); setPanel(null); }} className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[13px] font-normal text-texto hover:bg-slate-50">📝 Borradores de capítulos</button>}
            <div className="mx-2 my-1 h-px bg-slate-100" />
            {confirmando === '__todo__' ? (
              <div className="flex items-center justify-between gap-1 rounded-[10px] bg-red-50 px-3 py-2">
                <span className="text-[11.5px] text-red-700">Se borra para TODO el equipo.</span>
                <button type="button" onClick={() => void borrarTodo()} className="rounded-lg bg-red-600 px-2 py-1 text-[11.5px] font-medium text-white">Borrar</button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmando('__todo__')} className="flex w-full items-center gap-2 rounded-[10px] px-3 py-2 text-left text-[13px] font-normal text-gris hover:bg-red-50 hover:text-red-600">🗑 Borrar todo el historial</button>
            )}
          </div>
        )}
      </header>

      {/* Avisos flotantes bajo la cabecera */}
      {(estudiando || hayGenerando || aviso || error) && (
        <div className="pointer-events-none absolute inset-x-0 top-16 z-10 flex flex-col items-center gap-1.5 px-4">
          {estudiando && <p role="status" className="pointer-events-auto rounded-xl border border-celeste/60 bg-[#F2F9FD] px-4 py-2 text-[12.5px] font-normal text-azul-noche shadow-sm">🔄 {agente} está estudiando documentos nuevos — puedes conversar igual.</p>}
          {/* Progreso PERSISTENTE de generación de capítulos: la redacción tarda ~2-3 min;
              sin esta barra el proceso parece congelado/caído (no lo está). */}
          {hayGenerando && (() => {
            const gen = borradores.filter((b) => b.estado === 'generando');
            const info = gen[0] ? capInfo(gen[0].capitulo) : null;
            return (
              <div role="status" className="pointer-events-auto flex max-w-[540px] items-center gap-2.5 rounded-xl border border-celeste/60 bg-[#F2F9FD] px-4 py-2.5 text-[12.5px] font-normal text-azul-noche shadow-sm">
                <Spinner tamano="xs" />
                <span>
                  <b>{info?.agente ?? 'El redactor'}</b> está redactando {gen.length > 1 ? `${gen.length} capítulos` : `el ${info?.titulo ?? 'capítulo'}`}…{' '}
                  <span className="text-gris">suele tardar 2-3 min. Puedes seguir conversando; te aviso al terminar.</span>
                </span>
                <button type="button" onClick={() => setSheet('borradores')} className="ml-1 shrink-0 rounded-full border border-azul-profundo/30 px-2.5 py-0.5 text-[11.5px] font-medium text-azul-profundo hover:bg-celeste/15">Ver progreso</button>
              </div>
            );
          })()}
          {aviso && <p role="status" className="pointer-events-auto rounded-xl border border-celeste/60 bg-[#F2F9FD] px-4 py-2 text-[12.5px] font-normal text-azul-noche shadow-sm">{aviso}</p>}
          {error && <p role="alert" className="pointer-events-auto rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-[12.5px] font-normal text-red-700 shadow-sm">{error}</p>}
        </div>
      )}

      {/* ── Conversación a lo ancho (columna centrada) ── */}
      <div ref={contRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-4">
        <div className="mx-auto flex w-full max-w-[820px] flex-col gap-4">
          {mensajes.length === 0 && !pensando && (
            <div className="flex flex-col items-center gap-4 py-10">
              <p className="text-center text-sm font-light text-gris">
                {placeholderVacio ?? `Pregúntale a ${agente} sobre esta DIA — p. ej. "¿Cuál es el titular del proyecto y qué superficie ocupa?"`}
              </p>
              {(badgesIniciales?.length ?? 0) > 0 && (
                <div className="flex flex-col items-stretch gap-2 sm:max-w-md">
                  {badgesIniciales!.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => { setTexto(b); composerRef.current?.focus(); }}
                      className="rounded-xl border border-celeste/60 bg-white px-4 py-2.5 text-left text-[13px] font-normal text-azul-profundo transition-colors hover:bg-celeste/10"
                    >
                      {b}
                    </button>
                  ))}
                </div>
              )}
              {vacioExtra}
            </div>
          )}
          {mensajes.map((m) => (
            <div key={m.id} className="contents">
              <div data-msg className={`group/wrap ${m.rol === 'user' ? 'ml-auto max-w-[85%]' : 'mr-auto w-full'} ${esCompactado(m) ? 'opacity-55' : ''}`}>
                {m.rol === 'andres' && <TimelineEventos m={m} />}
                {m.rol === 'andres' ? (
                  // Sin texto real todavía (arranque o entre herramientas): indicador de
                  // actividad en vez del feo "… ▌". Con texto: la burbuja con streaming.
                  (m.estado === 'generando' && (!m.contenido || m.contenido === '…' || m.contenido.trim() === '')) ? (
                    <IndicadorActividad m={m} agente={agente} />
                  ) : (
                    <div className="group/msg relative rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-normal leading-relaxed text-texto">
                      <TextoFluido texto={m.contenido} generando={m.estado === 'generando'} />
                      {m.estado === 'listo' && (
                        <span className="absolute -top-2.5 right-3 hidden gap-1 group-hover/msg:flex">
                          <button type="button" onClick={() => void descargarPdfMensaje(m)} disabled={pdfDescargando === m.id} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10.5px] font-medium text-azul-profundo shadow-sm hover:bg-celeste/15 disabled:opacity-60" title="Descargar esta respuesta como PDF">
                            {pdfDescargando === m.id ? 'Generando…' : '⬇ PDF'}
                          </button>
                          <button type="button" onClick={() => void borrarMensaje(m)} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10.5px] font-medium text-gris shadow-sm hover:text-red-600" title="Borrar esta respuesta">🗑</button>
                        </span>
                      )}
                    </div>
                  )
                ) : editandoMsg === m.id ? (
                  <div className="rounded-2xl border-2 border-azul-profundo bg-white p-2">
                    <textarea autoFocus value={msgDraft} onChange={(e) => setMsgDraft(e.target.value)} rows={Math.min(8, Math.max(2, msgDraft.split('\n').length))} className="w-full resize-none border-0 bg-transparent p-1 text-sm text-azul-noche outline-none" aria-label="Editar mensaje" />
                    <div className="flex justify-end gap-2 pt-1">
                      <button type="button" onClick={() => setEditandoMsg(null)} className="rounded-lg px-2.5 py-1 text-[12px] text-gris hover:bg-slate-50">Cancelar</button>
                      <button type="button" onClick={() => void reenviarMensaje(m, msgDraft.trim())} disabled={!msgDraft.trim()} className="rounded-lg bg-azul-profundo px-3 py-1 text-[12px] font-medium text-white disabled:opacity-50">Guardar y reenviar</button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-azul-profundo px-4 py-3 text-sm font-normal leading-relaxed text-white">
                    <p className="whitespace-pre-wrap">{m.contenido}</p>
                    {(m.adjuntos?.length ?? 0) > 0 && (
                      <p className="mt-1.5 flex flex-wrap gap-1.5">
                        {m.adjuntos!.map((a, i) => (
                          <span key={i} className="rounded-full bg-white/15 px-2 py-0.5 text-[10.5px]">📎 {a.nombre.slice(0, 32)}</span>
                        ))}
                      </p>
                    )}
                  </div>
                )}
                {m.rol === 'user' && editandoMsg !== m.id && !m.id.startsWith('tmp-') && !pensando && (
                  <div className="mt-0.5 hidden justify-end gap-3 pr-1.5 group-hover/wrap:flex">
                    <button type="button" onClick={() => { setEditandoMsg(m.id); setMsgDraft(m.contenido); }} className="text-[11px] font-normal text-gris/70 transition-colors hover:text-azul-profundo" title="Editar y reenviar desde aquí (borra lo posterior)">✎ editar</button>
                    <button type="button" onClick={() => void reenviarMensaje(m)} className="text-[11px] font-normal text-gris/70 transition-colors hover:text-azul-profundo" title="Relanzar desde aquí (borra lo posterior)">↻ relanzar</button>
                    <button type="button" onClick={() => void borrarMensaje(m)} className="text-[11px] font-normal text-gris/70 transition-colors hover:text-red-600" title="Borrar mensaje y su respuesta">borrar</button>
                  </div>
                )}
                {m.rol === 'andres' && m.estado === 'listo' && (m.acciones?.length ?? 0) > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {m.acciones!.map((a, i) => {
                      const ejecutando = a.tipo === 'generar_capitulo' && hayGenerando;
                      // Chip de aclaración (tipo "responder"): al tocarlo, su etiqueta se envía como
                      // respuesta del usuario y Andrés continúa. La recomendada va resaltada.
                      if (a.tipo === 'responder') {
                        return (
                          <button
                            key={i}
                            type="button"
                            disabled={pensando}
                            onClick={() => void enviar(a.etiqueta)}
                            className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[12.5px] font-medium transition-colors disabled:opacity-50 ${a.recomendada ? 'bg-azul-profundo text-white hover:bg-azul-noche' : 'border border-azul-profundo/30 bg-white text-azul-profundo hover:bg-celeste/15'}`}
                            title="Responder con un click"
                          >
                            {a.recomendada && <span aria-hidden="true">★</span>}
                            {a.etiqueta}
                          </button>
                        );
                      }
                      const icono = a.tipo === 'generar_capitulo' ? '📝' : a.tipo === 'comprimir_contexto' ? '🗜' : '⬇';
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={ejecutando}
                          onClick={() => {
                            if (a.tipo === 'generar_capitulo' && a.capitulo) void generarCapitulo(a.capitulo, a.instrucciones);
                            else if (a.tipo === 'comprimir_contexto') void comprimir();
                            else if (a.tipo === 'pdf_respuesta') void descargarPdfMensaje(m);
                          }}
                          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-azul-profundo/30 bg-white px-3.5 text-[12.5px] font-medium text-azul-profundo transition-colors hover:bg-celeste/15 disabled:opacity-50"
                          title={ejecutando ? 'Ya hay un capítulo redactándose…' : 'Ejecutar con un click'}
                        >
                          {ejecutando ? <Spinner tamano="xs" /> : <span aria-hidden="true">{icono}</span>}
                          {a.etiqueta}
                        </button>
                      );
                    })}
                    {hayGenerando && (
                      <button type="button" onClick={() => setSheet('borradores')} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 text-[12.5px] font-medium text-gris hover:bg-slate-50">
                        Ver borradores →
                      </button>
                    )}
                  </div>
                )}
                {m.rol === 'andres' && (m.citas?.length ?? 0) > 0 && (
                  <div className="mt-1.5 flex flex-col gap-1">
                    {m.citas!.map((c, i) => (
                      <details key={i} className="rounded-lg border border-slate-200 bg-[#FAFBFC] px-3 py-1.5">
                        <summary className="cursor-pointer text-[11px] font-medium text-azul-profundo">{c.nombre.startsWith('🌐') || c.nombre.startsWith('💾') ? '' : '📄 '}{c.nombre}</summary>
                        <p className="mt-1 text-[11px] font-light leading-relaxed text-gris">…{c.extracto}…</p>
                        {c.ruta.startsWith('http') ? (
                          <a href={c.ruta} target="_blank" rel="noopener noreferrer" className="mt-0.5 block truncate text-[10px] font-normal text-azul-profundo hover:underline">{c.ruta}</a>
                        ) : (
                          <p className="mt-0.5 truncate text-[10px] font-normal text-gris/70">{c.ruta}</p>
                        )}
                      </details>
                    ))}
                  </div>
                )}
              </div>
              {m.id === ultimoCompactado && (
                <div className="my-1 flex items-center gap-2 text-[11px] font-normal text-gris">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="flex flex-wrap items-center justify-center gap-x-2">
                    🗜 Memo comprimió esta conversación — lo esencial quedó en la Memoria de la DIA
                    <details className="inline">
                      <summary className="cursor-pointer font-medium text-azul-profundo">Ver qué recuerda</summary>
                      <div className="chat-md mt-2 rounded-xl border border-slate-200 bg-[#FAFBFC] p-3 text-left text-[12px]">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentesEnlaces}>{chatInfo?.resumen_contexto ?? ''}</ReactMarkdown>
                      </div>
                    </details>
                    <button type="button" onClick={() => setSheet('memoria')} className="font-medium text-azul-profundo hover:underline">Abrir Memoria →</button>
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
              )}
            </div>
          ))}
          {pensando && !mensajes.some((m) => m.estado === 'generando') && (
            <div className="mr-auto flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
              <Spinner tamano="xs" />
              <span className="text-sm font-light text-gris">{agente} está buceando en los documentos…</span>
            </div>
          )}
          <div ref={finRef} />
        </div>
      </div>

      {/* ── Composer (tarjeta viva) ── */}
      <div className="shrink-0 px-3 pb-3 sm:px-4">
        <div className="mx-auto w-full max-w-[820px]">
          {(adjuntos.length > 0 || subiendo) && (
            <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
              {adjuntos.map((a, i) => (
                <span key={i} className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-normal text-texto">
                  📎 {a.nombre.slice(0, 34)}
                  <button type="button" onClick={() => setAdjuntos((x) => x.filter((_, j) => j !== i))} className="text-gris hover:text-red-600" aria-label={`Quitar ${a.nombre}`}>✕</button>
                </span>
              ))}
              {subiendo && <span className="flex items-center gap-1.5 text-[11px] text-gris"><Spinner tamano="xs" /> subiendo…</span>}
            </div>
          )}
          <div className="flex items-end gap-1.5 rounded-2xl border border-slate-200 bg-white px-2 py-1.5 shadow-sm transition-colors focus-within:border-azul-profundo focus-within:ring-[3px] focus-within:ring-azul-profundo/10">
            <button type="button" onClick={() => adjRef.current?.click()} disabled={!chatId || subiendo} className="shrink-0 rounded-full p-2 text-gris transition-colors hover:bg-celeste/10 hover:text-azul-profundo disabled:opacity-40" title="Adjuntar archivos o imágenes (también puedes pegar con Ctrl+V)" aria-label="Adjuntar archivos">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <input ref={adjRef} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files?.length) void subirAdjuntos(e.target.files); e.target.value = ''; }} />
            <textarea
              ref={composerRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onPaste={alPegar}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); void enviar(); } }}
              placeholder={`Pregúntale a ${agente}… (Enter = nueva línea · Shift+Enter = enviar)`}
              rows={textoInicial ? 3 : 1}
              className="max-h-48 flex-1 resize-none border-0 bg-transparent py-2 text-[14px] font-normal leading-relaxed text-azul-noche outline-none placeholder:text-gris/50"
            />
            {pensando && (
              <button
                type="button"
                onClick={() => void cancelar()}
                className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-[12.5px] font-medium text-gris transition-colors hover:border-red-300 hover:text-red-600"
                title="Detener la respuesta en curso"
                aria-label="Detener"
              >
                <span aria-hidden="true" className="h-2.5 w-2.5 rounded-[2px] bg-current" /> Detener
              </button>
            )}
            <button
              type="button"
              onClick={() => void enviar()}
              disabled={subiendo || !texto.trim()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-azul-profundo text-white transition-colors hover:bg-azul-noche disabled:bg-slate-200 disabled:text-slate-400"
              title={pensando ? 'Enviar (se contestará a continuación)' : 'Enviar'}
              aria-label="Enviar"
            >
              ➤
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* ── Panel derecho: capítulos de la DIA (desktop; en mobile queda el sheet ⋯) ── */}
      {!sinCapitulos && <aside className="hidden w-[290px] shrink-0 flex-col border-l border-slate-200/70 bg-[#FAFBFC] lg:flex">
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200/70 px-4">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-gris">Capítulos de la DIA</span>
          <button type="button" onClick={() => setSheet('borradores')} className="rounded-lg px-2 py-1 text-[11.5px] font-medium text-azul-profundo transition-colors hover:bg-celeste/10" title="Redactar un capítulo con los agentes">+ Redactar</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {borradores.length === 0 ? (
            <p className="px-1 py-8 text-center text-[12px] font-light leading-relaxed text-gris">Aún no hay capítulos. Pídele a Andrés que redacte uno, o usa <span className="font-medium text-azul-profundo">+ Redactar</span>.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {borradores.map((b) => (
                <li key={b.id} className="rounded-xl border border-slate-200 bg-white p-2.5 transition-colors hover:border-celeste">
                  <button type="button" onClick={() => void abrirBorrador(b)} className="block w-full text-left text-[12.5px] font-medium leading-snug text-azul-noche hover:text-azul-profundo" title="Ver el borrador">
                    {capInfo(b.capitulo).titulo}
                  </button>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[10.5px] text-gris">v{b.version} · {capInfo(b.capitulo).agente} · {fechaRel(b.created_at)}</span>
                    {b.estado === 'generando' ? <Spinner tamano="xs" /> : b.estado === 'error'
                      ? <span className="text-[10px] font-medium text-red-600">error</span>
                      : <span className="text-[10px] font-medium text-emerald-600">listo ✓</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setTexto((t) => (t.trim() ? t.trimEnd() + '\n' : '') + `Sobre el ${capInfo(b.capitulo).titulo}: `); composerRef.current?.focus(); }}
                    className="mt-1.5 rounded-md bg-celeste/15 px-2 py-0.5 text-[10.5px] font-medium text-azul-profundo transition-colors hover:bg-celeste/30"
                    title="Insertar una referencia a este capítulo en el chat"
                  >
                    ↳ referenciar en el chat
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>}

      {/* ── Sheet: Memoria de la DIA ── */}
      {sheet === 'memoria' && (
        <Modal titulo={`🧠 ${etiquetaMemoria} · ${nombre}`} onCerrar={() => { setSheet(null); setEditandoMemoria(false); }} ancho="xl">
          <p className="text-[12px] font-normal leading-relaxed text-gris">
            Se escribe sola: cada vez que Memo comprime una conversación, destila aquí lo que {agente} debe
            recordar siempre (proyecto, cifras, decisiones, pendientes). Puedes corregirla cuando quieras.
          </p>
          {memoriaAt && (
            <p className="mt-2"><span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">Actualizada · {fechaRel(memoriaAt)}</span></p>
          )}
          {editandoMemoria ? (
            <>
              <textarea value={memoriaDraft} onChange={(e) => setMemoriaDraft(e.target.value)} rows={14} className="campo mt-3 !h-auto max-h-[50vh] resize-y font-mono !text-[12.5px] leading-relaxed" aria-label="Memoria de la DIA (markdown)" />
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => setEditandoMemoria(false)} className="btn-fantasma">Cancelar</button>
                <button type="button" onClick={() => void guardarMemoria()} disabled={guardandoMemoria} className="btn-primario disabled:opacity-50">{guardandoMemoria ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </>
          ) : (
            <>
              <div className="chat-md mt-3 max-h-[52vh] overflow-y-auto rounded-xl border border-slate-100 bg-white p-4 text-sm">
                {memoria.trim() ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentesEnlaces}>{memoria}</ReactMarkdown>
                ) : (
                  <p className="text-[13px] font-light text-gris">Todavía no hay memoria — se creará sola con la primera compresión, o escríbela tú con Corregir.</p>
                )}
              </div>
              <div className="mt-3 flex justify-end">
                <button type="button" onClick={() => { setMemoriaDraft(memoria); setEditandoMemoria(true); }} className="btn-secundario">✎ Corregir</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Sheet: Borradores de capítulos ── */}
      {sheet === 'borradores' && (
        <Modal titulo="📝 Borradores de capítulos" onCerrar={() => setSheet(null)}>
          <p className="text-[12px] font-normal leading-relaxed text-gris">
            Roberto y Diamela redactan la rev_0 aprendiendo de los casos de referencia. Todo dato citado; lo faltante queda como [FALTA].
          </p>
          <textarea
            value={instruccionesCap}
            onChange={(e) => setInstruccionesCap(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder={'Instrucciones opcionales para el redactor — ej: "usa principalmente la carpeta Cap1_Material"'}
            className="mt-3 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-[12.5px] text-texto placeholder:text-gris/50 focus:border-azul-profundo focus:outline-none focus:ring-2 focus:ring-azul-profundo/20"
          />
          <div className="mt-2 flex flex-col gap-1.5 sm:flex-row">
            <button type="button" onClick={() => { void generarCapitulo('cap1', instruccionesCap); setInstruccionesCap(''); }} disabled={hayGenerando} className="btn-secundario flex-1 !text-[12.5px] disabled:opacity-50">Redactar Cap. 1 · Roberto</button>
            <button type="button" onClick={() => { void generarCapitulo('cap2', instruccionesCap); setInstruccionesCap(''); }} disabled={hayGenerando} className="btn-secundario flex-1 !text-[12.5px] disabled:opacity-50">Redactar Cap. 2 · Diamela</button>
          </div>
          {borradores.length > 0 && (
            <ul className="mt-3 flex max-h-[45vh] flex-col gap-1 overflow-y-auto">
              {borradores.map((b) => (
                <li key={b.id} className="flex items-stretch gap-1.5">
                  <button type="button" onClick={() => void abrirBorrador(b)} className="flex flex-1 items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 text-left hover:border-celeste">
                    <span className="text-[12.5px] font-normal text-azul-noche">{capInfo(b.capitulo).titulo} <span className="text-gris">v{b.version} · {fechaRel(b.created_at)}</span></span>
                    {b.estado === 'generando' ? <Spinner tamano="xs" /> : b.estado === 'error'
                      ? <span className="text-[10.5px] font-medium text-red-600">error</span>
                      : <span className="text-[10.5px] font-medium text-emerald-600">listo ✓</span>}
                  </button>
                  <a href={`/agentes/dia/${favoritoId}/capitulo/${b.capitulo}/preview`} target="_blank" rel="noopener noreferrer" title="Previsualizar en otra pestaña (se actualiza en vivo)" className="flex shrink-0 items-center rounded-xl border border-slate-200 px-2.5 text-[13px] text-azul-profundo hover:border-celeste hover:bg-celeste/10">↗</a>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}

      {/* Visor de borradores de capítulo */}
      {verBorrador && (
        <Modal titulo={`${capInfo(verBorrador.capitulo).titulo} · v${verBorrador.version} · ${capInfo(verBorrador.capitulo).agente}`} onCerrar={() => setVerBorrador(null)} ancho="xl" ayuda={AYUDA_PROCEDENCIA}>
          {verBorrador.estado === 'generando' && (
            <p role="status" className="mb-3 flex items-center gap-2 rounded-lg bg-celeste/15 px-3 py-2 text-sm text-azul-noche"><Spinner tamano="xs" /> Redactando en vivo…</p>
          )}
          {verBorrador.estado === 'error' && <p role="alert" className="alerta-error mb-3">{verBorrador.error ?? 'La generación falló.'}</p>}
          <div className="chat-md max-h-[58vh] overflow-y-auto rounded-xl border border-slate-100 bg-white p-4 text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={componentesMarcas(verBorrador.fuentes)}>{conMarcas(verBorrador.contenido)}</ReactMarkdown>
          </div>
          <ListaFuentes fuentes={verBorrador.fuentes} className="mt-2" />
          <div className="mt-4 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <button type="button" onClick={() => void eliminarBorrador(verBorrador)} className="text-xs font-normal text-gris hover:text-red-600">Eliminar</button>
            <div className="flex gap-2">
              <button type="button" onClick={() => void publicarEnSharePoint(verBorrador)} disabled={verBorrador.estado !== 'listo' || publicando} className="btn-secundario disabled:opacity-50" title="Publica el borrador en <proyecto>/Capítulos DIA - Agentes">
                {publicando ? 'Publicando…' : '💾 Enviar a SharePoint'}
              </button>
              <button type="button" onClick={() => void descargarArchivo(`/api/dia/capitulos/${verBorrador.id}/docx`, 'borrador.docx')} disabled={verBorrador.estado !== 'listo'} className="btn-secundario disabled:opacity-50" title="Word con formato AMBEC">Descargar Word</button>
              <a href={`/agentes/dia/${favoritoId}/capitulo/${verBorrador.capitulo}/preview`} target="_blank" rel="noopener noreferrer" className="btn-secundario" title="Abre una pestaña que se actualiza sola mientras editas">↗ Previsualizar</a>
              <button type="button" onClick={() => setVerBorrador(null)} className="btn-fantasma">Cerrar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
